/**
 * 预算成分输入组件
 * 用于项目表单中设置预算成分明细
 * 支持自动/手动预算模式
 */
import React, { useEffect, useState } from 'react';
import {
  Typography,
  Alert,
  Collapse,
  Tag
} from 'antd';
import {
  LockOutlined
} from '@ant-design/icons';
import { BudgetMode, type BudgetBreakdown } from '../../types';

const { Text } = Typography;
const { Panel } = Collapse;

// 预算成分配置
const BUDGET_ITEMS = [
  { key: 'labor' as keyof BudgetBreakdown, label: '人工', shortLabel: '人工', color: '#1677FF' },
  { key: 'equipment' as keyof BudgetBreakdown, label: '机械', shortLabel: '机械', color: '#52C41A' },
  { key: 'material' as keyof BudgetBreakdown, label: '材料', shortLabel: '材料', color: '#FA8C16' },
  { key: 'subcontract' as keyof BudgetBreakdown, label: '分包', shortLabel: '分包', color: '#13C2C2' },
  { key: 'coordination' as keyof BudgetBreakdown, label: '协调', shortLabel: '协调', color: '#722ED1' },
  { key: 'contingency' as keyof BudgetBreakdown, label: '应急', shortLabel: '应急', color: '#EB2F96' }
];

// 默认预算成分
const DEFAULT_BREAKDOWN: BudgetBreakdown = {
  labor: 0,
  equipment: 0,
  material: 0,
  subcontract: 0,
  coordination: 0,
  contingency: 0
};

interface BudgetBreakdownInputProps {
  value?: BudgetBreakdown;
  onChange?: (value: BudgetBreakdown) => void;
  totalBudget?: number;
  onTotalBudgetChange?: (value: number) => void;
  budgetMode?: BudgetMode;
  onBudgetModeChange?: (mode: BudgetMode) => void;
  budgetLocked?: boolean;
  disabled?: boolean;
  isMobile?: boolean;
  showModeSwitch?: boolean;
  showLockStatus?: boolean;
}

const BudgetBreakdownInput: React.FC<BudgetBreakdownInputProps> = ({
  value,
  onChange,
  totalBudget = 0,
  onTotalBudgetChange,
  budgetMode = BudgetMode.AUTO,
  onBudgetModeChange,
  budgetLocked = false,
  disabled = false,
  isMobile = false,
  showModeSwitch = true,
  showLockStatus = true
}) => {
  const [breakdown, setBreakdown] = useState<BudgetBreakdown>(DEFAULT_BREAKDOWN);
  const [isAutoMode, setIsAutoMode] = useState(budgetMode === BudgetMode.AUTO);

  useEffect(() => {
    if (value) {
      setBreakdown({
        labor: value.labor || 0,
        equipment: value.equipment || 0,
        material: value.material || 0,
        subcontract: value.subcontract || 0,
        coordination: value.coordination || 0,
        contingency: value.contingency || 0
      });
    }
  }, [value]);

  useEffect(() => {
    setIsAutoMode(budgetMode === BudgetMode.AUTO);
  }, [budgetMode]);

  const total = breakdown.labor + breakdown.equipment + breakdown.material +
    breakdown.subcontract + breakdown.coordination + breakdown.contingency;

  const difference = totalBudget - total;
  const isMatched = Math.abs(difference) < 0.01;

  const handleItemChange = (key: keyof BudgetBreakdown, newValue: number) => {
    const updatedBreakdown = { ...breakdown, [key]: newValue };
    setBreakdown(updatedBreakdown);
    onChange?.(updatedBreakdown);

    if (isAutoMode && onTotalBudgetChange) {
      const newTotal = updatedBreakdown.labor + updatedBreakdown.equipment +
        updatedBreakdown.material + updatedBreakdown.subcontract +
        updatedBreakdown.coordination + updatedBreakdown.contingency;
      onTotalBudgetChange(newTotal);
    }
  };

  const handleModeChange = (checked: boolean) => {
    const newMode: BudgetMode = checked ? BudgetMode.AUTO : BudgetMode.MANUAL;
    setIsAutoMode(checked);
    onBudgetModeChange?.(newMode);

    if (checked && onTotalBudgetChange) {
      onTotalBudgetChange(total);
    }
  };

  const syncTotalBudget = () => {
    if (onTotalBudgetChange) {
      onTotalBudgetChange(total);
    }
  };

  const getPercentage = (amount: number): number => {
    if (total <= 0) return 0;
    return (amount / total) * 100;
  };

  const formatMoney = (amount: number): string => {
    if (amount >= 10000) {
      return `¥${(amount / 10000).toFixed(1)}万`;
    }
    return `¥${amount.toLocaleString()}`;
  };

  const getBudgetHealthStatus = () => {
    if (total === 0) return { status: 'default', text: '未设置预算' };
    if (isMatched) return { status: 'success', text: '预算匹配' };
    if (difference > 0) return { status: 'warning', text: `少分配 ¥${difference.toLocaleString()}` };
    return { status: 'error', text: `超分配 ¥${Math.abs(difference).toLocaleString()}` };
  };

  const healthStatus = getBudgetHealthStatus();

  return (
    <Collapse
      defaultActiveKey={['breakdown']}
      style={{ marginTop: 8 }}
    >
      <Panel
        header={
          <span style={{ fontSize: 13, fontWeight: 500 }}>
            预算成分明细
            {showLockStatus && budgetLocked && (
              <Tag icon={<LockOutlined />} color="red" style={{ marginLeft: 8 }}>已锁定</Tag>
            )}
            {total > 0 && (
              <Tag
                color={
                  healthStatus.status === 'success' ? 'green' :
                    healthStatus.status === 'warning' ? 'orange' :
                      healthStatus.status === 'error' ? 'red' : 'default'
                }
                style={{ marginLeft: 8 }}
              >
                {healthStatus.text}
              </Tag>
            )}
          </span>
        }
        key="breakdown"
      >
        {/* 预算成分输入列表 */}
        <div className="breakdown-list">
          {BUDGET_ITEMS.map((item) => {
            const amount = breakdown[item.key];
            const pct = getPercentage(amount);
            return (
              <div key={item.key} className="breakdown-item">
                <div className="breakdown-item-dot" style={{ background: item.color }} />
                <div className="breakdown-item-name">{item.label}</div>
                <div className="breakdown-item-bar">
                  <div
                    className="breakdown-item-bar-fill"
                    style={{
                      width: `${pct}%`,
                      background: item.color
                    }}
                  />
                </div>
                <div className="breakdown-item-value-wrap">
                  <span className="breakdown-item-pct">{pct.toFixed(1)}%</span>
                  <input
                    type="number"
                    className="breakdown-item-input"
                    value={amount || ''}
                    placeholder="0"
                    min={0}
                    onChange={(e) => handleItemChange(item.key, parseFloat(e.target.value) || 0)}
                    disabled={disabled || budgetLocked}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* 汇总行 */}
        <div className="breakdown-summary-row">
          <div className="breakdown-summary-item">
            <span className="breakdown-summary-label">预算成分合计</span>
            <span className="breakdown-summary-value">{formatMoney(total)}</span>
          </div>
          <div className="breakdown-summary-divider" />
          <div className="breakdown-summary-item">
            <span className="breakdown-summary-label">项目总预算</span>
            <div className="breakdown-summary-value-row">
              <span className="breakdown-summary-value">{formatMoney(totalBudget)}</span>
              {!isAutoMode && !isMatched && !budgetLocked && (
                <button className="breakdown-sync-btn" onClick={syncTotalBudget}>同步</button>
              )}
            </div>
          </div>
        </div>

        {/* 匹配提示 */}
        {(totalBudget > 0 || total > 0) && (
          <Alert
            type={isMatched ? 'success' : (difference < 0 ? 'error' : 'warning')}
            message={
              isMatched
                ? '预算成分合计与总预算完全匹配'
                : `差异金额: ${formatMoney(Math.abs(difference))} (${difference < 0 ? '超分配' : '少分配'})`
            }
            style={{ marginTop: 10, fontSize: 12 }}
            showIcon={false}
          />
        )}

        {/* 提示 */}
        <div className="breakdown-tip">
          <span>💡 提示：{isAutoMode
            ? '自动模式下，修改任意预算成分会自动更新总预算'
            : '手动模式下，总预算可独立设置，建议与成分合计保持一致'}
          </span>
        </div>
      </Panel>
    </Collapse>
  );
};

export default BudgetBreakdownInput;
